import { Dayjs } from 'dayjs';
import { DateTime } from 'luxon';
import { STATION_TIME_ZONE, pickedDay, stationInstant } from './stationTime';

const MINUTES_PER_DAY = 1440;
const HH_MM = /^\d{1,2}:\d{2}$/;

export interface VisitorBookingWindow {
    fromDate: Date;
    toDate: Date;
}

/**
 * The window a visitor (locker) booking is created for — ONE definition, used
 * by the order the saga sends (`createOrderFlow`) and by the ticket the
 * operator confirms (`BookingPage`).
 *
 * Start: the picked day at the picked time slot, on the station's clock
 * (ADR-0019). The availability the page offers is keyed the same way — a
 * full-day group publishes one slot, `00:00`.
 *
 * End:
 * - a whole number of days from midnight — what a full-day group sells — runs
 *   to the end of the last station day it covers. That is the window the
 *   availability endpoint checked (`startOfDay` → `endOfPreviousDay +
 *   duration`, availabilityService.ts), counted in calendar days so the 25-hour
 *   day of the October change is still one day;
 * - anything else ends `duration` minutes after the start.
 *
 * What it replaced always subtracted a day at the end: a 1-day booking on
 * 25.10.2026 ended an hour BEFORE it started, every duration under a day ended
 * on the previous day, and the picked time slot was ignored.
 *
 * @param picked the date picker's value (browser-local midnight of the day clicked)
 * @param bookingTime `HH:mm` of the picked slot; midnight when absent or not a time
 * @param durationMinutes the picked duration
 * @param timeZone the location's zone (#3219); the station default when omitted
 * @returns null while the inputs do not determine a window
 */
export const visitorBookingWindow = (
    picked: Dayjs | Date | null | undefined,
    bookingTime: string | null | undefined,
    durationMinutes: number,
    timeZone: string = STATION_TIME_ZONE,
): VisitorBookingWindow | null => {
    if (!picked || !Number.isFinite(durationMinutes) || durationMinutes <= 0) {
        return null;
    }
    const time = bookingTime && HH_MM.test(bookingTime) ? bookingTime : '00:00';
    const fromDate = stationInstant(pickedDay(picked), time, timeZone);
    const startsAtMidnight = DateTime.fromJSDate(fromDate, { zone: timeZone })
        .toFormat('HH:mm') === '00:00';

    if (startsAtMidnight && durationMinutes % MINUTES_PER_DAY === 0) {
        const toDate = DateTime.fromJSDate(fromDate, { zone: timeZone })
            .plus({ days: durationMinutes / MINUTES_PER_DAY })
            .minus({ milliseconds: 1 })
            .toJSDate();
        return { fromDate, toDate };
    }

    return { fromDate, toDate: new Date(fromDate.getTime() + durationMinutes * 60_000) };
};
